import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";
import { PageHeader, EmptyState, inputCls, btnPrimary } from "../../components/bits";
import { useAuth } from "../../context/AuthContext";
import { Lightbulb, ThumbsUp, Send, Trash2 } from "lucide-react";

export default function Proposals() {
  const { user } = useAuth();
  const [props, setProps] = useState(null);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/portal/proposals").then((r) => setProps(r.data)).catch((e) => toast.error(fmtErr(e))), []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/portal/proposals", { title, text });
      toast.success("تم نشر مقترحك — شكراً لمبادرتك");
      setTitle("");
      setText("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const vote = async (id) => {
    try {
      const { data } = await api.post(`/portal/proposals/${id}/vote`);
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف هذا المقترح؟")) return;
    try {
      await api.delete(`/portal/proposals/${id}`);
      toast.success("تم حذف المقترح");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!props) return <div className="text-center text-mutext py-20 font-head">جارٍ التحميل …</div>;

  return (
    <div data-testid="proposals-page">
      <PageHeader title="المقترحات والتصويت" sub="قناة مفتوحة بين المنسقين — اقترح، وصوّت لما يخدم العمل التطوعي" />

      <div className="bg-surface rounded-2xl border border-brd shadow-md p-4 mb-6">
        <div className="flex gap-3 items-start">
          <div className="w-10 h-10 rounded-xl bg-coral-100 dark:bg-surface2 flex items-center justify-center shrink-0">
            <Lightbulb className="w-5 h-5 text-coral-900 dark:text-coral-300" />
          </div>
          <div className="flex-1">
            <input data-testid="proposal-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="عنوان المقترح" className={`${inputCls} mb-2`} />
            <textarea data-testid="proposal-text-input" value={text} onChange={(e) => setText(e.target.value)}
              placeholder="اكتب مقترحك لتطوير المنصة أو العمل التطوعي…" className={inputCls} rows={2} />
            <div className="text-left mt-2">
              <button data-testid="proposal-submit-btn" onClick={submit} disabled={busy || text.trim().length < 10} className={btnPrimary}>
                <Send className="w-4 h-4" /> نشر المقترح
              </button>
            </div>
          </div>
        </div>
      </div>

      {props.length === 0 ? (
        <EmptyState testid="proposals-empty" text="لا توجد مقترحات بعد — كن أول من يقترح" />
      ) : (
        <div className="space-y-3">
          {props.map((p) => (
            <div key={p.id} data-testid={`proposal-card-${p.id.slice(0, 8)}`}
              className="bg-surface rounded-2xl border border-brd shadow-md p-4 flex items-center gap-4">
              <button data-testid={`proposal-vote-${p.id.slice(0, 8)}`} onClick={() => vote(p.id)}
                className={`w-14 shrink-0 rounded-xl py-2 flex flex-col items-center gap-0.5 transition-colors ${
                  p.has_voted ? "bg-coral-600 text-white" : "bg-beige-600 dark:bg-surface2 text-turq-900 dark:text-turq-300 hover:bg-beige-900/50"}`}>
                <ThumbsUp className="w-4 h-4" />
                <span className="font-head font-black text-sm">{p.votes_count}</span>
              </button>
              <div className="flex-1">
                {p.title && <div className="font-head font-bold text-sm mb-1">{p.title}</div>}
                <p className="text-sm leading-relaxed">{p.text}</p>
                <div className="text-[10.5px] text-mutext mt-1.5">{p.author_name} — {p.school_name} · {(p.created_at || "").slice(0, 10)}</div>
              </div>
              {p.author_id === user?.id && (
                <button data-testid={`proposal-delete-${p.id.slice(0, 8)}`} onClick={() => remove(p.id)}
                  className="w-8 h-8 rounded-lg bg-coral-100 dark:bg-surface2 flex items-center justify-center shrink-0">
                  <Trash2 className="w-3.5 h-3.5 text-coral-900 dark:text-coral-300" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
